import _ from 'lodash';

import useApi from '@/shared/composables/useApi';

/**
 * Loads questions for a Schema-owned synced model from the shared question_sets endpoint.
 *
 * The endpoint at /api/shared/question_sets returns questions that are:
 *   - Filtered by the current member's role permissions
 *   - Filtered by published status (specialists may opt in to unpublished)
 *   - Already condition-evaluated against the provided draft_data
 *
 * This composable always sends the in-memory record as draft_data (never synced_model_id),
 * so it works for any schematized data type without requiring per-type CurrentMember
 * scoping methods on the backend.
 *
 * @param {Ref<string>} schemaIdRef     - id of the Schema that owns the questions
 * @param {Ref<string>} syncedModelRef  - data type name (e.g. 'PublicRecord', 'Form')
 * @param {Ref<object>} recordRef       - reactive record sent as draft_data
 * @param {boolean} includeUnpublished      - opt-in for specialist preview of unpublished questions
 * @param {boolean} refetchOnRecordChange   - refetch when draft_data changes (default false)
 * @param {number} debounceMs               - delay for record-driven refetches when enabled (default 400)
 *
 * @returns {{
 *   questions: Ref<Array>
 *   questionsLoaded: Ref<boolean>
 *   loadQuestions: () => Promise<void>
 *   cancelPendingRefetch: () => void
 * }}
 */
export default function useQuestionSet({
  schemaIdRef,
  syncedModelRef,
  recordRef,
  includeUnpublished = false,
  refetchOnRecordChange = false,
  debounceMs = 400,
}) {
  const { api } = useApi();
  const questions = ref([]);
  const questionsLoaded = ref(false);
  let isLoading = false;

  async function loadQuestions() {
    if (!schemaIdRef.value || !syncedModelRef.value) return;
    if (isLoading) return;

    isLoading = true;
    try {
      const response = await api.shared.questionSet.index({
        owner_type: 'Schema',
        owner_id: schemaIdRef.value,
        synced_model: syncedModelRef.value,
        draft_data: recordRef.value ?? {},
        include_unpublished: includeUnpublished,
      });

      if (response?.data) {
        questions.value = response.data;
        questionsLoaded.value = true;
      }
    } finally {
      isLoading = false;
    }
  }

  const debouncedLoadQuestions = refetchOnRecordChange
    ? _.debounce(loadQuestions, debounceMs)
    : null;

  watch([schemaIdRef, syncedModelRef], () => {
    void loadQuestions();
  });

  if (refetchOnRecordChange) {
    watch(
      recordRef,
      () => {
        if (!questionsLoaded.value) return;
        debouncedLoadQuestions();
      },
      { deep: true },
    );
  }

  function cancelPendingRefetch() {
    debouncedLoadQuestions?.cancel();
  }

  return {
    questions,
    questionsLoaded,
    loadQuestions,
    cancelPendingRefetch,
  };
}
